import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ShoppingCart, DollarSign, ArrowRight, Store, Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function CompraVenta() {
  const [params, setParams] = useSearchParams();
  const op = params.get("op"); // compra | venta
  const market = params.get("market"); // detalle | mayor
  const navigate = useNavigate();
  const { canSeeMarket, isOpMayor, isOpDetalle } = useAuth();
  const availableMarkets = [
    { value: "detalle", label: "Detalle", icon: Store, text: "Compra/venta con precio en pesos dominicanos (DOP)" },
    { value: "mayor", label: "Mayor", icon: Building2, text: "Compra/venta con porcentaje sobre USDT (USD)" },
  ].filter((item) => canSeeMarket(item.value));

  if (!op) {
    return (
      <div className="space-y-8" data-testid="compraventa-step1">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Nueva Operación</h1>
          <p className="text-slate-500 mt-1">Selecciona el tipo de operación</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            data-testid="select-compra"
            onClick={() => setParams({ op: "compra" })}
            className="group p-10 bg-white border border-slate-200 rounded-2xl hover:border-emerald-500 hover:shadow-lg transition-all text-left"
          >
            <div className="w-14 h-14 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center mb-6 group-hover:scale-110 transition-transform">
              <ShoppingCart size={26} />
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-2">Compra</div>
            <p className="text-sm text-slate-500">Registrar entrada de inventario al recibir USDT</p>
            <div className="mt-6 text-emerald-600 font-semibold text-sm flex items-center gap-1">
              Continuar <ArrowRight size={16} />
            </div>
          </button>
          <button
            data-testid="select-venta"
            onClick={() => setParams({ op: "venta" })}
            className="group p-10 bg-white border border-slate-200 rounded-2xl hover:border-rose-500 hover:shadow-lg transition-all text-left"
          >
            <div className="w-14 h-14 rounded-xl bg-rose-50 text-rose-600 grid place-items-center mb-6 group-hover:scale-110 transition-transform">
              <DollarSign size={26} />
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-2">Venta</div>
            <p className="text-sm text-slate-500">Registrar salida de inventario al despachar USDT</p>
            <div className="mt-6 text-rose-600 font-semibold text-sm flex items-center gap-1">
              Continuar <ArrowRight size={16} />
            </div>
          </button>
        </div>
      </div>
    );
  }

  if (!market) {
    return (
      <div className="space-y-8" data-testid="compraventa-step2">
        <div>
          <button
            onClick={() => setParams({})}
            className="text-sm text-slate-500 hover:text-slate-900 mb-2"
            data-testid="back-step1"
          >
            ← Volver
          </button>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight capitalize">
            {op} — Seleccionar Mercado
          </h1>
        </div>
        <div className={`grid grid-cols-1 ${availableMarkets.length > 1 ? "md:grid-cols-2" : "max-w-xl"} gap-6`}>
          {availableMarkets.map(({ value, label, icon: Icon, text }) => (
            <button
              key={value}
              data-testid={`select-${value}`}
              onClick={() => navigate(`/transaccion?op=${op}&market=${value}`)}
              className="group p-10 bg-white border border-slate-200 rounded-2xl hover:border-slate-900 hover:shadow-lg transition-all text-left"
            >
              <div className="w-14 h-14 rounded-xl bg-slate-100 text-slate-700 grid place-items-center mb-6 group-hover:scale-110 transition-transform">
                <Icon size={26} />
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-2">{label}</div>
              <p className="text-sm text-slate-500">{text}</p>
            </button>
          ))}
          {(isOpMayor || isOpDetalle) && availableMarkets.length === 1 && (
            <p className="text-sm text-slate-500">Tu rol solo permite registrar operaciones en mercado {availableMarkets[0].label.toLowerCase()}.</p>
          )}
        </div>
      </div>
    );
  }
  return null;
}
